import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { apiFetch, getToken } from '../../api/client';
import API_CONFIG from '../../config/api';
import { withUnidade } from '../../utils/storageByUnit';
import { useAuth, getUserRole } from '../../contexts/AuthContext';
import { isFiscalSetorial, getFiscalSetor, withSetorScope } from '../../utils/dataIsolation';
import { formatCurrency, parseValorMes, useModuloFiscalData } from './useModuloFiscalData';
import {
  MESES_COMPETENCIA,
  normalizarCompetencia,
  extrairAnoCompetencia,
} from '../../utils/competenciaUtils';
import './moduloFiscal.css';

const CRITERIOS = [
  { key: 'criterio1', label: 'Serviço conforme o contrato' },
  { key: 'criterio2', label: 'Rotina / programação de execução' },
  { key: 'criterio3', label: 'Qualidade do serviço/produto' },
  { key: 'criterio4', label: 'Comunicação acessível e ágil' },
  { key: 'criterio5', label: 'Empenho na resolução de problemas' },
  { key: 'criterio6', label: 'Confiabilidade e entrega' },
  { key: 'criterio7', label: 'Segurança na execução' },
  { key: 'criterio8', label: 'Alinhamento estratégico' },
];

function Stars({ value, onChange }) {
  return (
    <div className="mf-stars" role="group">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`mf-star${n <= value ? ' mf-star--on' : ''}`}
          onClick={() => onChange(n)}
          aria-label={`${n} estrela${n > 1 ? 's' : ''}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

async function openPdfComAuth(url, unidadeCodigo) {
  const token = getToken();
  try {
    const abs = new URL(url, window.location.origin);
    if (token) abs.searchParams.set('access_token', token);
    if (unidadeCodigo && !abs.searchParams.has('unidade')) {
      abs.searchParams.set('unidade', unidadeCodigo);
    }
    window.open(abs.toString(), '_blank', 'noopener,noreferrer');
  } catch (e) {
    console.error(e);
    alert('Erro ao abrir PDF.');
  }
}

/**
 * Notas Fiscais do módulo isolado do fiscal:
 * seleciona contrato → NFs (ARCA) → PDF consolidado → autorizar/recusar
 * (com PDFs contrato/aditivo) → avaliação mensal (estrelas, glosa, %).
 */
export default function ModuloFiscalNotasFiscaisPage() {
  const { fiscalId } = useParams();
  const navigate = useNavigate();
  const { unidadeSelecionada, user, rbacRole, fiscalSetor } = useAuth();
  const { loading: loadingBase, error: errorBase, fiscal, contratos } = useModuloFiscalData(fiscalId);

  const [contratoId, setContratoId] = useState('');
  const [notas, setNotas] = useState([]);
  const [notasTodas, setNotasTodas] = useState([]);
  const [atividades, setAtividades] = useState([]);
  const [loadingNf, setLoadingNf] = useState(false);
  const [aditivos, setAditivos] = useState([]);
  const [msg, setMsg] = useState('');
  const anoAtual = String(new Date().getFullYear());
  const [filtroAno, setFiltroAno] = useState(anoAtual);
  const [filtroMes, setFiltroMes] = useState(''); // '' = todos os meses

  const [modalDec, setModalDec] = useState(null); // { atividade, nota }
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [observacao, setObservacao] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [modalAval, setModalAval] = useState(null); // nota
  const [avalForm, setAvalForm] = useState({
    criterio1: 3,
    criterio2: 3,
    criterio3: 3,
    criterio4: 3,
    criterio5: 3,
    criterio6: 3,
    criterio7: 3,
    criterio8: 3,
    glosa: '',
    percentualBom: '',
    percentualRuim: '',
    valorNotaSelecionado: '',
    pontosPositivos: '',
    pontosNegativos: '',
  });
  const [salvandoAval, setSalvandoAval] = useState(false);

  const contratoSel = useMemo(
    () => contratos.find((c) => String(c.id) === String(contratoId)) || null,
    [contratos, contratoId]
  );

  const scopeOpts = useMemo(
    () => ({ user, rbacRole: rbacRole || getUserRole(user), fiscalSetor }),
    [user, rbacRole, fiscalSetor]
  );

  const rankingGeral = useMemo(() => {
    const vals = CRITERIOS.map((c) => Number(avalForm[c.key]) || 0);
    const media = vals.reduce((a, b) => a + b, 0) / vals.length;
    return Math.round(media * 10) / 10;
  }, [avalForm]);

  const loadNotasEAtividades = useCallback(async () => {
    if (!contratos.length) {
      setNotas([]);
      setNotasTodas([]);
      setAtividades([]);
      setAditivos([]);
      return;
    }
    setLoadingNf(true);
    setMsg('');
    try {
      const numsSetor = new Set(contratos.map((c) => String(c.numeroContrato)));

      // Todas as NFs da unidade, filtradas pelos contratos do setor do fiscal
      const pathAll = withUnidade('/api/notas-fiscais', unidadeSelecionada);
      const pathAtiv = withSetorScope(
        withUnidade('/api/notas-fiscais/atividades-processo-pagamento?status=TODOS', unidadeSelecionada),
        scopeOpts
      );

      const [resAll, resAt] = await Promise.all([apiFetch(pathAll), apiFetch(pathAtiv)]);

      let todas = [];
      if (resAll.ok) {
        const data = await resAll.json();
        const list = Array.isArray(data) ? data : data?.notas || data?.data || [];
        todas = list.filter((n) => {
          const num = String(n.numeroContrato || n.numero_contrato || '');
          return numsSetor.has(num);
        });
      }
      setNotasTodas(todas);

      if (contratoSel?.numeroContrato) {
        setNotas(
          todas.filter(
            (n) =>
              String(n.numeroContrato || n.numero_contrato) === String(contratoSel.numeroContrato)
          )
        );
        const pathAd = withUnidade(
          `/api/aditivos?numeroContrato=${encodeURIComponent(contratoSel.numeroContrato)}`,
          unidadeSelecionada
        );
        const resAd = await apiFetch(pathAd);
        if (resAd.ok) {
          const ads = await resAd.json();
          setAditivos(Array.isArray(ads) ? ads : []);
        } else setAditivos([]);
      } else {
        setNotas([]);
        setAditivos([]);
      }

      let listaAt = [];
      if (resAt.ok) {
        const data = await resAt.json();
        listaAt = Array.isArray(data) ? data : [];
      }
      const idsNf = new Set(
        (contratoSel
          ? todas.filter(
              (n) =>
                String(n.numeroContrato || n.numero_contrato) ===
                String(contratoSel.numeroContrato)
            )
          : todas
        ).map((n) => String(n.id))
      );
      listaAt = listaAt.filter((a) => {
        const nid = a.nota_fiscal_id_principal || a.notaFiscalIdPrincipal;
        if (nid && idsNf.has(String(nid))) return true;
        const num = a.numero_contrato || a.numeroContrato;
        return num && numsSetor.has(String(num));
      });
      setAtividades(listaAt);
    } catch (e) {
      console.error(e);
      setMsg(e.message || 'Erro ao carregar notas fiscais.');
      setNotas([]);
      setNotasTodas([]);
      setAtividades([]);
    } finally {
      setLoadingNf(false);
    }
  }, [contratos, contratoSel, unidadeSelecionada, scopeOpts]);

  useEffect(() => {
    loadNotasEAtividades();
  }, [loadNotasEAtividades]);

  const anosDisponiveis = useMemo(() => {
    const set = new Set();
    notasTodas.forEach((n) => {
      const a = extrairAnoCompetencia(n.competencia || n.competencia_mes);
      if (a) set.add(a);
    });
    if (!set.has(anoAtual)) set.add(anoAtual);
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [notasTodas, anoAtual]);

  const biBase = useMemo(() => {
    const fonte = contratoSel
      ? notasTodas.filter(
          (n) =>
            String(n.numeroContrato || n.numero_contrato) === String(contratoSel.numeroContrato)
        )
      : notasTodas;

    const filtradas = fonte.filter((n) => {
      const comp = n.competencia || n.competencia_mes;
      const ano = extrairAnoCompetencia(comp);
      if (filtroAno && ano !== filtroAno) return false;
      if (filtroMes) {
        const norm = normalizarCompetencia(comp);
        if (!norm || !norm.toLowerCase().startsWith(filtroMes.toLowerCase())) return false;
      }
      return true;
    });

    const totalBruto = filtradas.reduce(
      (s, n) => s + (parseFloat(n.valor_bruto) || parseValorMes(n.valor_bruto) || 0),
      0
    );
    const totalLiquido = filtradas.reduce(
      (s, n) =>
        s +
        (parseFloat(n.valor_liquido) ||
          parseValorMes(n.valor_liquido ?? n.valorLiquido) ||
          0),
      0
    );

    const porMes = MESES_COMPETENCIA.map((mes) => {
      const doMes = filtradas.filter((n) => {
        const norm = normalizarCompetencia(n.competencia || n.competencia_mes);
        return norm && norm.toLowerCase().startsWith(mes.toLowerCase());
      });
      return {
        mes,
        qtd: doMes.length,
        bruto: doMes.reduce((s, n) => s + (parseFloat(n.valor_bruto) || 0), 0),
        liquido: doMes.reduce(
          (s, n) => s + (parseFloat(n.valor_liquido) || parseValorMes(n.valor_liquido) || 0),
          0
        ),
      };
    }).filter((r) => r.qtd > 0 || !filtroMes);

    return {
      filtradas,
      totalBruto,
      totalLiquido,
      qtd: filtradas.length,
      porMes: filtroMes ? porMes.filter((r) => r.mes === filtroMes) : porMes,
      escopo: contratoSel
        ? `Contrato ${contratoSel.numeroContrato}`
        : 'Todos os contratos do setor',
    };
  }, [notasTodas, contratoSel, filtroAno, filtroMes]);

  useEffect(() => {
    if (!modalDec?.atividade?.notaFiscalIdPrincipal && !modalDec?.nota?.id) {
      setPdfBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return undefined;
    }
    const notaId =
      modalDec.nota?.id ||
      modalDec.atividade?.nota_fiscal_id_principal ||
      modalDec.atividade?.notaFiscalIdPrincipal;
    if (!notaId) return undefined;
    let cancelled = false;
    setLoadingPdf(true);
    apiFetch(`/api/notas-fiscais/${notaId}/pdf-consolidado`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (cancelled || !blob) return;
        setPdfBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      })
      .finally(() => {
        if (!cancelled) setLoadingPdf(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalDec]);

  const atividadePorNota = useMemo(() => {
    const map = {};
    atividades.forEach((a) => {
      const nid = a.nota_fiscal_id_principal || a.notaFiscalIdPrincipal;
      if (nid) map[String(nid)] = a;
    });
    return map;
  }, [atividades]);

  const abrirDecisao = (nota) => {
    const atv = atividadePorNota[String(nota.id)];
    setObservacao('');
    setModalDec({ nota, atividade: atv || null });
  };

  const fecharDecisao = () => {
    setModalDec(null);
    setObservacao('');
    setPdfBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const enviarDecisao = async (acao) => {
    const atv = modalDec?.atividade;
    if (!atv?.id) {
      setMsg('Não há atividade de processo de pagamento pendente para esta NF.');
      return;
    }
    const obs = String(observacao || '').trim();
    if (!obs) {
      setMsg('Informe a observação (obrigatória para auditoria).');
      return;
    }
    setEnviando(true);
    setMsg('');
    try {
      const res = await apiFetch(`/api/notas-fiscais/atividades/${atv.id}/decisao`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ acao, usuarioEmail: user?.email, observacao: obs }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao registrar decisão');

      const nota = modalDec.nota;
      fecharDecisao();
      await loadNotasEAtividades();

      if (acao === 'AUTORIZAR' && data.precisaAvaliacao !== false && nota) {
        setAvalForm((f) => ({
          ...f,
          valorNotaSelecionado: String(nota.valor_liquido ?? nota.valorLiquido ?? ''),
          glosa: '',
          percentualBom: '',
          percentualRuim: '',
        }));
        setModalAval(nota);
      } else {
        setMsg(data.message || (acao === 'AUTORIZAR' ? 'NF autorizada.' : 'NF recusada.'));
      }
    } catch (e) {
      setMsg(e.message || 'Erro ao decidir.');
    } finally {
      setEnviando(false);
    }
  };

  const salvarAvaliacao = async () => {
    if (!modalAval || !contratoSel) return;
    setSalvandoAval(true);
    setMsg('');
    try {
      const payload = {
        notaFiscalId: modalAval.id,
        fiscalId: user?.id || fiscalId,
        fiscalNome: user?.nome || user?.email || fiscal?.nome || 'Fiscal',
        empresa: modalAval.empresa || contratoSel.empresa,
        setor: contratoSel.setor || fiscal?.setor || 'Geral',
        notaAvaliacao: rankingGeral,
        comentario: `Glosa: ${avalForm.glosa || '—'}\n% Bom: ${avalForm.percentualBom || '—'}\n% Ruim: ${avalForm.percentualRuim || '—'}\nValor NF selecionado: ${avalForm.valorNotaSelecionado || '—'}\n\nPositivos: ${avalForm.pontosPositivos}\nNegativos: ${avalForm.pontosNegativos}`,
        criterio1: avalForm.criterio1,
        criterio2: avalForm.criterio2,
        criterio3: avalForm.criterio3,
        criterio4: avalForm.criterio4,
        criterio5: avalForm.criterio5,
        criterio6: avalForm.criterio6,
        criterio7: avalForm.criterio7,
        criterio8: avalForm.criterio8,
        dataAvaliacao: new Date().toISOString().split('T')[0],
        pontosPositivos: avalForm.pontosPositivos,
        pontosNegativos: avalForm.pontosNegativos,
        numeroNotaFiscal: modalAval.numero_nota_fiscal || modalAval.numeroNotaFiscal,
        competencia: modalAval.competencia,
        valorLiquido: avalForm.valorNotaSelecionado || modalAval.valor_liquido,
        parcela: modalAval.parcela,
        glosa: avalForm.glosa,
        percentualBom: avalForm.percentualBom,
        percentualRuim: avalForm.percentualRuim,
        valorNotaSelecionado: avalForm.valorNotaSelecionado,
      };
      const res = await apiFetch(withUnidade('/api/avaliacoes-mensais', unidadeSelecionada), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar avaliação');
      setModalAval(null);
      setMsg('Avaliação mensal salva (módulo Contratos alimentado).');
      await loadNotasEAtividades();
      if (data.avaliacaoAssinaturaDocumentoId) {
        navigate(`/assinaturas/documentos/${data.avaliacaoAssinaturaDocumentoId}`, {
          state: { proximoAssinaturaDocumentoId: data.pagamentoAssinaturaDocumentoId || null },
        });
      }
    } catch (e) {
      setMsg(e.message || 'Erro ao salvar avaliação.');
    } finally {
      setSalvandoAval(false);
    }
  };

  if (loadingBase) {
    return (
      <div className="mf-page mf-page--embedded">
        <div className="mf-state">
          <CircularProgress sx={{ color: 'var(--ep-accent)' }} />
          <p>Carregando…</p>
        </div>
      </div>
    );
  }

  if (errorBase || !fiscal) {
    return (
      <div className="mf-page mf-page--embedded">
        <div className="mf-state">
          <p>{errorBase || 'Fiscal não encontrado.'}</p>
        </div>
      </div>
    );
  }

  const statusClass = (s) => {
    const u = String(s || '').toUpperCase();
    if (u.includes('PEND')) return 'mf-status--pendente';
    if (u.includes('AUTOR') || u.includes('PAG')) return 'mf-status--ok';
    if (u.includes('NEG') || u.includes('REJE')) return 'mf-status--neg';
    return 'mf-status--pendente';
  };

  return (
    <div className="mf-page mf-page--embedded">
      <header className="mf-page__header">
        <div>
          <h1 className="mf-page__title">Notas Fiscais — {fiscal.nome}</h1>
          <p className="mf-page__meta">
            Setor: <strong>{fiscal.setor || '—'}</strong> · NFs encaminhadas via ARCA/Contratos
          </p>
        </div>
      </header>

      <p className="mf-note">
        Dashboard BI do setor (multiunidade). Sem contrato: totais de todas as NFs. Com contrato:
        mesmos indicadores filtrados. Depois: PDF consolidado, autorizar/recusar e avaliação mensal.
      </p>

      {msg && <p className="mf-note">{msg}</p>}

      <div className="mf-bi-filters">
        <div>
          <label className="mf-kpi__label" htmlFor="mf-nf-ano">
            Ano
          </label>
          <select
            id="mf-nf-ano"
            className="mf-select mf-select--sm"
            value={filtroAno}
            onChange={(e) => setFiltroAno(e.target.value)}
          >
            {anosDisponiveis.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mf-kpi__label" htmlFor="mf-nf-mes">
            Mês
          </label>
          <select
            id="mf-nf-mes"
            className="mf-select mf-select--sm"
            value={filtroMes}
            onChange={(e) => setFiltroMes(e.target.value)}
          >
            <option value="">Todos os meses</option>
            {MESES_COMPETENCIA.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mf-kpi__label" htmlFor="mf-nf-contrato">
            Contrato
          </label>
          <select
            id="mf-nf-contrato"
            className="mf-select mf-select--sm"
            value={contratoId}
            onChange={(e) => setContratoId(e.target.value)}
          >
            <option value="">Todos os contratos do setor</option>
            {contratos.map((c) => (
              <option key={c.id} value={c.id}>
                {c.numeroContrato} — {c.empresa}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="mf-page__meta" style={{ marginBottom: '0.75rem' }}>
        Escopo: <strong>{biBase.escopo}</strong>
        {filtroMes ? ` · ${filtroMes}/${filtroAno}` : ` · Ano ${filtroAno}`}
      </p>

      <div className="mf-resumo">
        <div className="mf-kpi">
          <span className="mf-kpi__label">Qtd notas</span>
          <span className="mf-kpi__value">{loadingNf ? '…' : biBase.qtd}</span>
        </div>
        <div className="mf-kpi">
          <span className="mf-kpi__label">Valor bruto</span>
          <span className="mf-kpi__value">
            {loadingNf ? '…' : formatCurrency(biBase.totalBruto)}
          </span>
        </div>
        <div className="mf-kpi">
          <span className="mf-kpi__label">Valor líquido</span>
          <span className="mf-kpi__value">
            {loadingNf ? '…' : formatCurrency(biBase.totalLiquido)}
          </span>
        </div>
      </div>

      <h2 className="mf-section-title">Bruto × líquido por mês ({filtroAno})</h2>
      <div className="mf-table-wrap" style={{ marginBottom: '1.25rem' }}>
        {biBase.porMes.length === 0 ? (
          <div className="mf-empty">Sem notas no período selecionado.</div>
        ) : (
          <table className="mf-table">
            <thead>
              <tr>
                <th>Mês</th>
                <th>Qtd</th>
                <th>Bruto</th>
                <th>Líquido</th>
              </tr>
            </thead>
            <tbody>
              {biBase.porMes.map((r) => (
                <tr key={r.mes}>
                  <td>{r.mes}</td>
                  <td>{r.qtd}</td>
                  <td>{formatCurrency(r.bruto)}</td>
                  <td>{formatCurrency(r.liquido)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <h2 className="mf-section-title">
        {contratoSel ? 'Notas do contrato (decisão)' : 'Selecione um contrato para decidir NF'}
      </h2>

      {loadingNf && (
        <div className="mf-state">
          <CircularProgress sx={{ color: 'var(--ep-accent)' }} size={28} />
        </div>
      )}

      {!contratoId && !loadingNf && (
        <div className="mf-empty">
          Escolha um contrato acima para listar notas e autorizar/recusar.
        </div>
      )}

      {contratoId && !loadingNf && (
        <div className="mf-table-wrap">
          {notas.length === 0 ? (
            <div className="mf-empty">Nenhuma nota fiscal para este contrato nesta unidade.</div>
          ) : (
            <table className="mf-table">
              <thead>
                <tr>
                  <th>Nº NF</th>
                  <th>Competência</th>
                  <th>Bruto</th>
                  <th>Líquido</th>
                  <th>Status</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {notas
                  .filter((n) => {
                    const comp = n.competencia || n.competencia_mes;
                    const ano = extrairAnoCompetencia(comp);
                    if (filtroAno && ano !== filtroAno) return false;
                    if (filtroMes) {
                      const norm = normalizarCompetencia(comp);
                      if (!norm || !norm.toLowerCase().startsWith(filtroMes.toLowerCase())) {
                        return false;
                      }
                    }
                    return true;
                  })
                  .map((n) => {
                    const atv = atividadePorNota[String(n.id)];
                    const st = atv?.status || n.status || '—';
                    return (
                      <tr key={n.id}>
                        <td>{n.numero_nota_fiscal || n.numeroNotaFiscal || n.id}</td>
                        <td>{n.competencia || '—'}</td>
                        <td>{formatCurrency(parseFloat(n.valor_bruto) || 0)}</td>
                        <td>
                          {formatCurrency(
                            parseFloat(n.valor_liquido) ||
                              parseValorMes(n.valor_liquido ?? n.valorLiquido) ||
                              0
                          )}
                        </td>
                        <td>
                          <span className={`mf-status ${statusClass(st)}`}>{st}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="mf-btn mf-btn--ghost mf-btn--sm"
                            onClick={() => abrirDecisao(n)}
                          >
                            <ReceiptLongIcon sx={{ fontSize: 16 }} />
                            Abrir / decidir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modalDec && (
        <div className="mf-modal-backdrop" role="presentation" onClick={fecharDecisao}>
          <div
            className="mf-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mf-modal__head">
              <h2>
                Processo NF —{' '}
                {modalDec.nota?.numero_nota_fiscal ||
                  modalDec.atividade?.numero_processo ||
                  modalDec.nota?.id}
              </h2>
              <button type="button" className="mf-btn mf-btn--ghost mf-btn--sm" onClick={fecharDecisao}>
                Fechar
              </button>
            </div>
            <div className="mf-modal__body">
              <div className="mf-modal__pdf">
                {loadingPdf && <CircularProgress sx={{ color: 'var(--ep-accent)' }} />}
                {!loadingPdf && pdfBlobUrl && (
                  <iframe title="PDF consolidado" src={pdfBlobUrl} />
                )}
                {!loadingPdf && !pdfBlobUrl && (
                  <span className="mf-page__meta">PDF consolidado indisponível</span>
                )}
              </div>

              <p className="mf-kpi__label" style={{ marginTop: '0.85rem' }}>
                Conferência — contrato e aditivos
              </p>
              <div className="mf-modal__refs">
                {contratoSel && (
                  <button
                    type="button"
                    className="mf-btn mf-btn--ghost mf-btn--sm"
                    onClick={() =>
                      openPdfComAuth(
                        `${API_CONFIG.BACKEND_URL}/api/prestadores/${contratoSel.id}/contrato/pdf`,
                        unidadeSelecionada?.codigo
                      )
                    }
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                    Contrato primitivo
                  </button>
                )}
                {aditivos.map((ad) => (
                  <button
                    key={ad.id}
                    type="button"
                    className="mf-btn mf-btn--ghost mf-btn--sm"
                    onClick={() =>
                      openPdfComAuth(
                        `${API_CONFIG.BACKEND_URL}/api/aditivos/${ad.id}/pdf`,
                        unidadeSelecionada?.codigo
                      )
                    }
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                    Aditivo {ad.numeroAditivo || ad.id}
                  </button>
                ))}
                {aditivos.length === 0 && (
                  <span className="mf-page__meta">Sem aditivos cadastrados</span>
                )}
              </div>

              {(!modalDec.atividade ||
                String(modalDec.atividade.status || '').toUpperCase() === 'PENDENTE') && (
                <>
                  <label className="mf-kpi__label" htmlFor="mf-obs-dec">
                    Observação (obrigatória)
                  </label>
                  <textarea
                    id="mf-obs-dec"
                    className="mf-textarea"
                    rows={3}
                    value={observacao}
                    onChange={(e) => setObservacao(e.target.value)}
                    placeholder="Motivo da autorização ou da recusa…"
                  />
                </>
              )}
            </div>
            <div className="mf-modal__foot">
              <button type="button" className="mf-btn mf-btn--ghost" onClick={fecharDecisao}>
                Fechar
              </button>
              {modalDec.atividade &&
                String(modalDec.atividade.status || '').toUpperCase() === 'PENDENTE' && (
                  <>
                    <button
                      type="button"
                      className="mf-btn mf-btn--danger"
                      disabled={enviando || !observacao.trim()}
                      onClick={() => enviarDecisao('NEGAR')}
                    >
                      Recusar
                    </button>
                    <button
                      type="button"
                      className="mf-btn mf-btn--success"
                      disabled={enviando || !observacao.trim()}
                      onClick={() => enviarDecisao('AUTORIZAR')}
                    >
                      {enviando ? 'Enviando…' : 'Autorizar'}
                    </button>
                  </>
                )}
              {!modalDec.atividade && (
                <span className="mf-page__meta">Sem atividade pendente — só visualização do PDF</span>
              )}
            </div>
          </div>
        </div>
      )}

      {modalAval && (
        <div className="mf-modal-backdrop" role="presentation" onClick={() => setModalAval(null)}>
          <div className="mf-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="mf-modal__head">
              <h2>Avaliação mensal do fornecedor</h2>
              <button type="button" className="mf-btn mf-btn--ghost mf-btn--sm" onClick={() => setModalAval(null)}>
                Fechar
              </button>
            </div>
            <div className="mf-modal__body">
              <div className="mf-resumo" style={{ marginBottom: '1rem' }}>
                <div className="mf-kpi">
                  <span className="mf-kpi__label">Ranking geral</span>
                  <span className="mf-kpi__value">{rankingGeral} ★</span>
                </div>
                <div className="mf-kpi">
                  <span className="mf-kpi__label">NF</span>
                  <span className="mf-kpi__value mf-kpi__value--sm">
                    {modalAval.numero_nota_fiscal || modalAval.id}
                  </span>
                </div>
                <div className="mf-kpi">
                  <span className="mf-kpi__label">Competência</span>
                  <span className="mf-kpi__value mf-kpi__value--sm">
                    {modalAval.competencia || '—'}
                  </span>
                </div>
              </div>

              <div className="mf-trilha__grid" style={{ marginBottom: '1rem' }}>
                <div>
                  <span className="mf-trilha__label">Valor da NF (selecionado)</span>
                  <input
                    className="mf-input"
                    value={avalForm.valorNotaSelecionado}
                    onChange={(e) =>
                      setAvalForm((f) => ({ ...f, valorNotaSelecionado: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <span className="mf-trilha__label">Referência de glosa</span>
                  <input
                    className="mf-input"
                    value={avalForm.glosa}
                    onChange={(e) => setAvalForm((f) => ({ ...f, glosa: e.target.value }))}
                    placeholder="Ex.: 0 ou valor glosado"
                  />
                </div>
                <div>
                  <span className="mf-trilha__label">% Bom</span>
                  <input
                    className="mf-input"
                    value={avalForm.percentualBom}
                    onChange={(e) => setAvalForm((f) => ({ ...f, percentualBom: e.target.value }))}
                  />
                </div>
                <div>
                  <span className="mf-trilha__label">% Ruim</span>
                  <input
                    className="mf-input"
                    value={avalForm.percentualRuim}
                    onChange={(e) => setAvalForm((f) => ({ ...f, percentualRuim: e.target.value }))}
                  />
                </div>
              </div>

              {CRITERIOS.map((c) => (
                <div key={c.key} className="mf-criterio">
                  <span>{c.label}</span>
                  <Stars
                    value={avalForm[c.key]}
                    onChange={(n) => setAvalForm((f) => ({ ...f, [c.key]: n }))}
                  />
                </div>
              ))}

              <label className="mf-kpi__label" htmlFor="mf-pos" style={{ marginTop: '0.75rem' }}>
                Pontos positivos
              </label>
              <textarea
                id="mf-pos"
                className="mf-textarea"
                rows={2}
                value={avalForm.pontosPositivos}
                onChange={(e) => setAvalForm((f) => ({ ...f, pontosPositivos: e.target.value }))}
              />
              <label className="mf-kpi__label" htmlFor="mf-neg">
                Pontos negativos
              </label>
              <textarea
                id="mf-neg"
                className="mf-textarea"
                rows={2}
                value={avalForm.pontosNegativos}
                onChange={(e) => setAvalForm((f) => ({ ...f, pontosNegativos: e.target.value }))}
              />
            </div>
            <div className="mf-modal__foot">
              <button type="button" className="mf-btn mf-btn--ghost" onClick={() => setModalAval(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="mf-btn mf-btn--primary"
                disabled={salvandoAval}
                onClick={salvarAvaliacao}
              >
                {salvandoAval ? 'Salvando…' : 'Salvar avaliação'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
